/**
 * JOL Confidence — chargement des paires mots FR + EN.
 *
 * Format : 1 fichier JSON par lang dans `apps/web/public/dictionaries/`,
 * importé en build-time par Vite.
 *
 * Banque dédiée (50 paires par lang, 5 tiers de difficulté). Pas de réuse
 * du dictionnaire word-pyramids qui est orienté anagrammes.
 */
import pairsFrRaw from '../../../public/dictionaries/jol-words-fr.json';
import pairsEnRaw from '../../../public/dictionaries/jol-words-en.json';
const BANKS = {
    fr: pairsFrRaw,
    en: pairsEnRaw,
};
export function getBank(lang) {
    return BANKS[lang];
}
/**
 * Sélectionne `n` paires de la difficulty demandée. Si pas assez de
 * candidats à cette difficulty exacte, complète avec la difficulty
 * inférieure puis supérieure.
 */
export function pickPairs(lang, difficulty, n, seed) {
    const bank = getBank(lang);
    const exact = bank.pairs.filter((p) => p.difficulty === difficulty);
    const out = [];
    const seen = new Set();
    function rotateAndPush(source, offset) {
        if (source.length === 0)
            return;
        for (let i = 0; i < source.length && out.length < n; i += 1) {
            const idx = ((offset + i) >>> 0) % source.length;
            const p = source[idx];
            if (seen.has(p.id))
                continue;
            seen.add(p.id);
            out.push(p);
        }
    }
    rotateAndPush(exact, seed);
    if (out.length < n) {
        const lower = bank.pairs.filter((p) => p.difficulty < difficulty);
        rotateAndPush(lower, seed + 1);
    }
    if (out.length < n) {
        const higher = bank.pairs.filter((p) => p.difficulty > difficulty);
        rotateAndPush(higher, seed + 2);
    }
    return out.slice(0, n);
}
/**
 * Normalise une réponse user pour comparaison : lowercase + retire
 * accents + retire ponctuation/espace.
 */
export function normalizeAnswer(input) {
    return input
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z]/g, '');
}
/**
 * Compare une réponse user au target (insensible à la casse + accents).
 */
export function isCorrectAnswer(answer, target) {
    return normalizeAnswer(answer) === normalizeAnswer(target);
}
/**
 * Calcule le gamma de Goodman-Kruskal entre prédictions JOL et accuracy.
 *
 * Pour chaque paire d'items (i, j), on compare :
 *   - concordant si jol[i] > jol[j] et accuracy[i] > accuracy[j] (ou inverse)
 *   - discordant si jol[i] > jol[j] et accuracy[i] < accuracy[j] (ou inverse)
 *
 * gamma = (concordant - discordant) / (concordant + discordant)
 *
 * Borne [-1, +1]. Retourne NaN si <2 items ou aucun pair concordant/discordant
 * (ex. tous les items ont la même accuracy ou la même JOL).
 */
export function computeGamma(items) {
    if (items.length < 2)
        return NaN;
    let concordant = 0;
    let discordant = 0;
    for (let i = 0; i < items.length; i += 1) {
        for (let j = i + 1; j < items.length; j += 1) {
            const a = items[i];
            const b = items[j];
            const dJol = a.jol - b.jol;
            const dAcc = (a.recalled ? 1 : 0) - (b.recalled ? 1 : 0);
            if (dJol === 0 || dAcc === 0)
                continue;
            if (Math.sign(dJol) === Math.sign(dAcc)) {
                concordant += 1;
            }
            else {
                discordant += 1;
            }
        }
    }
    const total = concordant + discordant;
    if (total === 0)
        return NaN;
    return (concordant - discordant) / total;
}
//# sourceMappingURL=data.js.map